import { Component } from 'preact'
import axios from 'axios'
import qs from 'qs'

import { AlertMessage, EscalateButton } from '../EscalationButtons'
import { ConfigContext, getFormattedTitle, showEscalateButton } from '../../utils'
import CreateProductboardNoteModal from './CreateProductboardNoteModal'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../../components/tracker/publish'
import { publish } from '@insided/deprecated-utils/globals'
import { ContentType } from '@insided/shared/utils/contentType'

interface Props {
  isEnabled: boolean
  title?: string
  username?: string
  userId: string
  content?: string | null
  publicId: number
  moderatorId: string
  isReply: boolean
  contentType: ContentType
  isTrashed: boolean
}

interface State {
  showModal: boolean
  productboardNoteUrl: string | null
  isLoading: boolean
  productboardContent: string | null
  productboardTitle?: string
  productboardTags?: string[]
  titleValidationError?: string
  contentValidationError?: string
  messageType?: string|null
  message?: string
}

class CreateProductNoteButton extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      showModal: false,
      productboardNoteUrl: null,
      isLoading: false,
      productboardTitle: getFormattedTitle(this.props.title, this.props.isReply),
      productboardContent: this.props.content ?? null
    }
    this.fetchProductboardNoteUrl = this.fetchProductboardNoteUrl.bind(this)
    this.createProductboardNote = this.createProductboardNote.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.onContentChange = this.onContentChange.bind(this)
    this.onChangeCallback = this.onChangeCallback.bind(this)
    this.onTextareaFocus = this.onTextareaFocus.bind(this)
    this.onChangeTags = this.onChangeTags.bind(this)
    this.onClickCallback = this.onClickCallback.bind(this)
  }

  async componentDidMount () {
    if (this.props.isEnabled) {
      await this.fetchProductboardNoteUrl()
    }
  }

  componentDidUpdate (previousProps: Readonly<Props>) {
    if (this.props.isTrashed !== previousProps.isTrashed) {
      this.setState({ messageType: null })
    }

    if (this.props.title !== previousProps.title) {
      this.setState({
        productboardTitle: getFormattedTitle(this.props.title, this.props.isReply)
      })
    }

    if (this.props.content !== previousProps.content) {
      this.setState({
        productboardContent: this.props.content ?? null
      })
    }
  }

  async fetchProductboardNoteUrl () {
    this.setState({ isLoading: true })
    try {
      const data = {
        postId: this.props.publicId,
        isReply: this.props.isReply,
        contentType: this.props.contentType,
        type: 'productboard'
      }
      const result = await axios.get('/escalation/fetch', { params: data })
      const noteUrl = result.data.ticketUrl
      this.setState({ productboardNoteUrl: noteUrl, isLoading: false })
      const isReplyToString = this.props.isReply.toString()
      const noteId = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('productboard_note_created', { url: noteUrl, id: noteId })
    } catch (e) {
      this.setState({ productboardNoteUrl: null, isLoading: false })
    }
  }

  async createProductboardNote () {
    this.setState({ messageType: null, titleValidationError: undefined, contentValidationError: undefined, isLoading: true })
    const { productboardTags, productboardContent, productboardTitle } = this.state
    const title = productboardTitle?.trim()
    const content = productboardContent?.trim()
    if (title === '' && content === '') {
      this.setState({
        contentValidationError: 'Please fill in the required field.',
        titleValidationError: 'Please fill in the required field.',
        isLoading: false
      })
      return
    }
    if (content === '') {
      this.setState({ contentValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    if (title === '') {
      this.setState({ titleValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    try {
      const { moderatorId, publicId, isReply, username, userId, contentType } = this.props

      const params = {
        moderatorId: moderatorId,
        postId: publicId,
        isReply: isReply,
        contentType: contentType,
        type: 'productboard',
        title: getFormattedTitle(productboardTitle),
        content: productboardContent,
        tags: productboardTags?.join(','),
        username: username,
        authorUserId: userId,
        link: window.location.href
      }
      const result = await axios.post('/escalation/add', qs.stringify(params), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      const noteUrl = result.data.ticketUrl
      this.setState({
        productboardNoteUrl: result.data.ticketUrl,
        showModal: false,
        messageType: 'success',
        message: 'You\'ve created a Productboard note.',
        isLoading: false
      })
      const isReplyToString = this.props.isReply.toString()
      const noteId = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('productboard_note_created', { url: noteUrl, id: noteId })
      tracker.trackEvent('userevent', {
        type: 'Product board note created'
      })
    } catch (e) {
      this.setState({
        productboardNoteUrl: null,
        showModal: false,
        messageType: 'error',
        message: e.response.data.error ?? 'Something went wrong, please try again.',
        isLoading: false
      })
    }
  }

  closeModal () {
    this.setState({
      showModal: false,
      productboardTags: [],
      productboardContent: this.props.content ?? '',
      productboardTitle: getFormattedTitle(this.props.title, this.props.isReply),
      contentValidationError: undefined,
      titleValidationError: undefined
    })
  }

  onChangeTags (values: string[]) {
    this.setState({ productboardTags: values })
  }

  onContentChange (value: string): void {
    this.setState({ productboardContent: value })
  }

  onTextareaFocus (e?: MouseEvent|FocusEvent) {
    const value = (e?.target as HTMLTextAreaElement).value
    this.setState({ contentValidationError: undefined, productboardContent: value })
  }

  onChangeCallback (value: string) {
    this.setState({ productboardTitle: value })
  }

  onClickCallback () {
    this.setState({ titleValidationError: undefined })
  }

  render () {
    const { username, isReply, isEnabled, contentType } = this.props
    const {
      isLoading, showModal, productboardTags, productboardContent, productboardTitle, messageType, message,
      productboardNoteUrl, titleValidationError, contentValidationError
    } = this.state

    if (!isEnabled) {
      return null
    }

    return (
      <ConfigContext.Consumer>
        {({ moderationConfig: { escalate, escalateReply } }) => {
          const showEscalationButton = showEscalateButton(escalate, escalateReply, isReply, Boolean(productboardNoteUrl), contentType)
          return (
            <div className='integration__escalate qa-escalate-productboard'>
              <AlertMessage messageType={messageType} message={message} />
              {showEscalationButton &&
                <div>
                  <EscalateButton
                    isDisabled={isLoading}
                    tooltipMessage='Create a Productboard note'
                    imageName='productboard-xs'
                    onClick={() => this.setState({ showModal: true, messageType: null })}
                    customClasses='qa-create-productboard-note'
                  />
                  <CreateProductboardNoteModal
                    isOpen={showModal}
                    closeModal={this.closeModal}
                    title={productboardTitle}
                    username={username}
                    content={productboardContent}
                    tags={productboardTags}
                    onChangeCallback={this.onChangeCallback}
                    onContentChange={this.onContentChange}
                    onSubmit={this.createProductboardNote}
                    onChangeTags={this.onChangeTags}
                    titleValidationError={titleValidationError}
                    contentValidationError={contentValidationError}
                    onTextareaFocus={this.onTextareaFocus}
                    onClickCallback={this.onClickCallback}
                    isLoading={isLoading}
                  />
                </div>}
            </div>
          )
        }}
      </ConfigContext.Consumer>
    )
  }
}

export default CreateProductNoteButton
